import { useState, type FormEvent } from "react";
import { Button, Card, DescriptionList, Notice, PageHeader, TextField, useToast } from "@plataforma/design-system";
import { PASSWORD_MIN_LENGTH } from "@plataforma/shared";
import { useAuth } from "../../app/auth";
import { HelpButton } from "../../app/help";
import { api, ApiError, errorMessage } from "../../lib/api";
import "./account.css";

const empty = { currentPassword: "", newPassword: "", confirmation: "" };

/** Configurações pessoais de quem está logado. Existe em toda aplicação, independente das funcionalidades. */
export function AccountPage() {
  const { user } = useAuth();
  const toast = useToast();
  const [values, setValues] = useState(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof empty | "form", string>>>({});
  const [saving, setSaving] = useState(false);
  const set = (field: keyof typeof empty) => (event: { target: { value: string } }) => setValues((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    const problems: typeof errors = {};
    if (!values.currentPassword) problems.currentPassword = "Informe a senha atual.";
    if (values.newPassword.length < PASSWORD_MIN_LENGTH) problems.newPassword = `Use pelo menos ${PASSWORD_MIN_LENGTH} caracteres.`;
    else if (values.newPassword === values.currentPassword) problems.newPassword = "A nova senha precisa ser diferente da atual.";
    if (values.confirmation !== values.newPassword) problems.confirmation = "A confirmação não é igual à nova senha.";
    setErrors(problems);
    if (Object.keys(problems).length) return;
    setSaving(true);
    try {
      await api.post("/auth/change-password", { currentPassword: values.currentPassword, newPassword: values.newPassword });
      setValues(empty);
      toast("Senha trocada. Os outros aparelhos em que a sua conta estava aberta foram desconectados.");
    } catch (failure) {
      if (failure instanceof ApiError && failure.code === "WRONG_CURRENT_PASSWORD") setErrors({ currentPassword: failure.message });
      else setErrors({ form: errorMessage(failure) });
    } finally {
      setSaving(false);
    }
  }

  if (!user) return null;
  return (
    <>
      <PageHeader title="Configurações pessoais" subtitle="Dados do seu acesso. Só você vê esta tela." actions={<HelpButton topic="account" />} />
      <div className="account-grid">
        <Card padded title="Sua conta">
          <div style={{ height: 12 }} />
          <DescriptionList items={[{ term: "Nome", value: user.name }, { term: "Função", value: user.roleLabel || "—" }, { term: "E-mail de acesso", value: user.email, wide: true }]} />
          <p className="ds-muted ds-small">Para mudar o nome ou o e-mail, peça a quem administra o sistema.</p>
        </Card>
        <Card padded title="Trocar senha">
          <form onSubmit={submit} noValidate style={{ marginTop: 12 }}>
            <TextField label="Senha atual" type="password" autoComplete="current-password" value={values.currentPassword} onChange={set("currentPassword")} error={errors.currentPassword} />
            <TextField label="Nova senha" type="password" autoComplete="new-password" value={values.newPassword} onChange={set("newPassword")} error={errors.newPassword} hint={`Pelo menos ${PASSWORD_MIN_LENGTH} caracteres. Uma frase com várias palavras funciona bem.`} />
            <TextField label="Confirmar a nova senha" type="password" autoComplete="new-password" value={values.confirmation} onChange={set("confirmation")} error={errors.confirmation} />
            {errors.form ? <div style={{ marginBottom: 12 }}><Notice tone="danger">{errors.form}</Notice></div> : null}
            <Button type="submit" variant="primary" disabled={saving}>{saving ? "Trocando…" : "Trocar senha"}</Button>
          </form>
        </Card>
      </div>
    </>
  );
}
